import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import { annotationSchema, annotationsFilter, annotationsFilterUiSchema } from '../Annotations';
import { headingsSchema } from '../Header';
import DistributionsTable from './DistributionsTable';

const DistributionsTableSchema: JSONSchema7 = {
    title: 'Distributions Table',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        downloadButtonName: {
            type: 'string',
            description: 'Download button text',
            default: 'Download Excel file',
        },
        downloadFileName: {
            type: 'string',
            title: 'Download File Name',
            default: '',
        },
        scheduleTitles: { ...headingsSchema, title: 'Schedule Title' },
        scheduleOptions: {
            type: 'array',
            title: 'Schedule Options',
            items: {
                type: 'object',
                properties: {
                    label: { type: 'string', title: 'Schedule Label' },
                    excelLabel: { type: 'string', title: 'Excel File Label' },
                    value: { type: 'string', title: 'Schedule data key' },
                },
            },
        },
        iconShowContent: {
            type: 'string',
            title: 'Show Content Icon Name',
            default: 'fas fa-chevron-right',
        },
        iconHideContent: {
            type: 'string',
            title: 'Hide Content Icon Name',
            default: 'fas fa-chevron-down',
        },
        titles: headingsSchema,
        columns: {
            type: 'array',
            title: 'Add columns',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'string', title: 'Column Id' },
                    label: { type: 'string', title: 'Column header text' },
                    excelLabel: { type: 'string', title: 'Excel File Column header text' },
                    value: { type: 'string', title: 'Column data key' },
                    groupValue: { type: 'string', title: 'Column data key for group' },
                    groupBy: { type: 'boolean', title: 'Group table data by', default: false },
                    filterBy: { type: 'boolean', title: 'Filter table data by', default: false },
                },
            },
        },
        tableDatakey: {
            type: 'string',
            description: 'Table data key',
        },
        disclaimer: {
            type: 'object',
            title: 'Disclaimer options',
            properties: {
                filterBy: annotationSchema,
                filters: {
                    ...annotationsFilter,
                    title: 'Filter By',
                },
            },
        },
        fileType: {
            type: 'string',
            enum: ['.xlsx', '.csv'],
            title: 'Download File Type',
        },
        transformData: {
            type: 'boolean',
            title: 'Transform Data',
            default: false,
        },
    },
    dependencies: {
        fileType: {
            oneOf: [
                {
                    properties: {
                        fileType: { enum: ['.xlsx'] },
                        excelTabs: {
                            type: 'array',
                            title: 'Excel sheet tabs option',
                            items: {
                                type: 'object',
                                properties: {
                                    name: {
                                        type: 'string',
                                        title: 'Sheet tab name',
                                    },
                                    data: {
                                        type: 'string',
                                        title: 'Data to show on sheet',
                                        enum: ['Schedule Options', 'Table Columns'],
                                    },
                                    showFundName: {
                                        type: 'boolean',
                                        title: 'Show fund and series name in first column',
                                    },
                                    showDisclaimer: {
                                        type: 'boolean',
                                        title: 'Show disclaimer',
                                    },
                                },
                            },
                        },
                    },
                },
                {
                    properties: {
                        fileType: { enum: ['.csv'] },
                        csvOptions: {
                            type: 'object',
                            properties: {
                                data: {
                                    type: 'string',
                                    title: 'Data to show on sheet',
                                    enum: ['Schedule Options', 'Table Columns'],
                                },
                                showFundName: {
                                    type: 'boolean',
                                    title: 'Show fund and series name in first column',
                                },
                                showDisclaimer: {
                                    type: 'boolean',
                                    title: 'Show disclaimer',
                                },
                            },
                        },
                    },
                },
            ],
        },
        transformData: {
            oneOf: [
                {
                    properties: {
                        transformData: { enum: [true] },
                        functionToTransformData: {
                            type: 'string',
                            description: 'Function to transform Data',
                            default: 'return (data) => { return data}',
                        },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'downloadButtonName',
            'downloadFileName',
            'scheduleTitles',
            'scheduleOptions',
            'iconShowContent',
            'iconHideContent',
            'titles',
            'columns',
            'tableDatakey',
            'disclaimer',
            'fileType',
            'excelTabs',
            'csvOptions',
        ],
        'Transform Data': ['transformData', 'functionToTransformData'],
    },
];

const DistributionsTableWidget: commonTypes.IWidget = {
    schema: DistributionsTableSchema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        functionToTransformData: { 'ui:widget': 'JSEditor' },
        disclaimer: { ...annotationsFilterUiSchema },
    },
    templateView: DistributionsTable,
    id: 'DistributionsTable',
    group: 'table',
    contentPlaceHolders: ['children'],
    initialData: () => ({
        columns: [{ label: 'header1' }, { label: 'header2' }, { label: 'header3' }],
    }),
};

export default DistributionsTableWidget;
